import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import type { BulkOfferResponse } from '@recruitee/bulk-actions-types';
import { I18nService } from '@recruitee/i18n';
import { OfferBulkActions, OfferBulkActionsService } from '@recruitee/offers-data';
import { DialogService, DialogWidth } from '@recruitee/user-interface';
import { generateUuid } from '@recruitee/utils';

import { ManageReferralsDialogComponent } from './manage-referrals-dialog/manage-referrals-dialog.component';

@Injectable()
export class ManageReferralsBulkService {
  private readonly confirmDialogTexts = {
    enable: {
      title: this.i18n.translate('app.offers.manage_referrals.confirm_enable.title'),
      message: this.i18n.translate('app.offers.manage_referrals.confirm_enable.message'),
    },
    disable: {
      title: this.i18n.translate('app.offers.manage_referrals.confirm_disable.title'),
      message: this.i18n.translate('app.offers.manage_referrals.confirm_disable.message'),
    },
  };

  constructor(
    private dialog: DialogService,
    private store: Store,
    private i18n: I18nService,
    private offerBulkActionsService: OfferBulkActionsService,
  ) {}

  public manageReferrals$({ ids }: { ids: number[] }): Observable<BulkOfferResponse | null> {
    return this.dialog
      .openCustom(
        ManageReferralsDialogComponent,
        {
          width: DialogWidth.Small,
        },
        {
          jobsCount: ids.length,
        },
      )
      .afterClosed.pipe(
        switchMap(action =>
          action
            ? this.confirm$({
                enable: action.enable,
                jobsCount: ids.length,
              }).pipe(map(confirmed => (confirmed ? action : null)))
            : of(null),
        ),
        switchMap(action =>
          action ? this.getRepoAction$({ enable: action.enable, ids }) : of(null),
        ),
      );
  }

  private getRepoAction$(props: { enable: boolean; ids: number[] }): Observable<BulkOfferResponse> {
    const correlationId = generateUuid();
    const action = props.enable
      ? OfferBulkActions.enableReferrals
      : OfferBulkActions.disableReferrals;
    this.store.dispatch(action({ payload: { correlationId, params: { ids: props.ids } } }));
    return this.offerBulkActionsService.trackByCorrelationId(correlationId);
  }

  private confirm$(props: { enable: boolean; jobsCount: number }): Observable<boolean> {
    const { enable, jobsCount } = props;
    const texts = enable ? this.confirmDialogTexts.enable : this.confirmDialogTexts.disable;
    const confirmConfig = {
      ...texts,
      agreeLabel: this.i18n.translate(
        enable
          ? 'app.offers.manage_referrals.confirm_enable.agree_label'
          : 'app.offers.manage_referrals.confirm_disable.agree_label',
        { count: jobsCount },
      ),
    };
    return enable ? this.dialog.information(confirmConfig) : this.dialog.warning(confirmConfig);
  }
}
